import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import UserJourneySankey from '../UserJourneySankey'
import {
  formatDate,
  getTagCategory,
  getTagCategoryColor,
  splitFlowDataByPages,
} from '../../../utils/analyticsUtils'
import {
  ADMIN_USER_ID,
  MAX_TOOLTIP_USERS,
} from '../../../config/adminConstants'

/**
 * MetricsDashboardSection - Displays comprehensive analytics metrics dashboard
 * Includes date filter, metric cards, charts, and detailed tables
 */
function MetricsDashboardSection({
  metrics = null,
  dailyActiveUsersDetailed = [],
  dailyActiveUsersLoading = false,
  players = [],
  eventsByUser = [],
  eventsByUserLoading = false,
  flowData = null,
  flowLoading = false,
  flowError = null,
  dateFilter = '30',
  onDateFilterChange = null,
  onJumpToUser = null,
  flowSearchTerm = '',
  onFlowSearchChange = null,
  flowSearchHint = '',
  onRefreshFlow = null,
}) {
  const landingAndAuthPages = useMemo(
    () => [
      '/landing',
      '/home',
      '/signup',
      '/sign-up',
      '/signin',
      '/sign-in',
      '/login',
    ],
    [],
  )

  const { selected: landingAuthFlow, remaining: journeyFlow } = useMemo(
    () => splitFlowDataByPages(flowData, landingAndAuthPages),
    [flowData, landingAndAuthPages],
  )

  const [excludeAdminTraffic, setExcludeAdminTraffic] = useState(true)
  const [showDetailedDau, setShowDetailedDau] = useState(false)
  const [expandedDates, setExpandedDates] = useState({})

  const playerMap = useMemo(() => {
    return new Map(players.map((player) => [String(player.user_id), player]))
  }, [players])

  const dauSeries = useMemo(() => {
    return dailyActiveUsersDetailed
      .map((day) => {
        const userIds = Array.isArray(day.user_ids)
          ? day.user_ids.map((id) => String(id))
          : []
        const filteredUserIds = excludeAdminTraffic
          ? userIds.filter((id) => id !== ADMIN_USER_ID)
          : userIds

        return {
          date: day.date,
          label: formatDate(day.date),
          userCount: filteredUserIds.length,
          topUsers: filteredUserIds
            .slice(0, MAX_TOOLTIP_USERS)
            .map((userId) => {
              const player = playerMap.get(userId)
              const name =
                `${player?.first_name || ''} ${player?.last_name || ''}`.trim()
              return {
                id: userId,
                label:
                  name ||
                  player?.email ||
                  player?.display_name ||
                  `User ${userId}`,
                email: player?.email,
              }
            }),
          users: filteredUserIds,
        }
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date))
  }, [dailyActiveUsersDetailed, excludeAdminTraffic, playerMap])

  const dauSummary = useMemo(() => {
    if (dauSeries.length === 0) {
      return { currentDay: 0, average: 0, changePct: 0 }
    }

    const counts = dauSeries.map((day) => day.userCount)
    const currentDay = counts[counts.length - 1] || 0
    const average =
      counts.reduce((sum, value) => sum + value, 0) / counts.length

    // Calculate period-over-period change by comparing first half vs second half averages
    // Example: For 30 days of data, compare days 1-15 avg to days 16-30 avg
    // This provides a simple trend indicator without requiring a baseline date
    const midpoint = Math.floor(counts.length / 2)
    const previous = counts.slice(0, midpoint)
    const current = counts.slice(midpoint)
    const previousAverage =
      previous.length > 0
        ? previous.reduce((sum, value) => sum + value, 0) / previous.length
        : 0
    const currentAverage =
      current.length > 0
        ? current.reduce((sum, value) => sum + value, 0) / current.length
        : 0
    const changePct =
      previousAverage > 0
        ? ((currentAverage - previousAverage) / previousAverage) * 100
        : 0

    return { currentDay, average, changePct }
  }, [dauSeries])

  const activeUserDays = useMemo(() => {
    return (
      metrics?.daily_active_users?.reduce(
        (total, day) => total + parseInt(day.daily_active_users),
        0,
      ) || 0
    )
  }, [metrics])

  const topFeature = useMemo(() => {
    if (!metrics?.top_features?.length) return null
    return metrics.top_features[0]
  }, [metrics])

  const toggleExpandedDate = (dateKey) => {
    setExpandedDates((prev) => ({ ...prev, [dateKey]: !prev[dateKey] }))
  }

  const handleShowDetailedDau = () => {
    setShowDetailedDau((prev) => {
      const newValue = !prev
      // Auto-expand most recent date when showing details for better UX
      if (newValue && dailyActiveUsersDetailed.length > 0) {
        const mostRecentDate =
          dailyActiveUsersDetailed[dailyActiveUsersDetailed.length - 1].date
        setExpandedDates((prevExpanded) => ({
          ...prevExpanded,
          [mostRecentDate]: true,
        }))
      }
      return newValue
    })
  }

  const DAUTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null
    const point = payload[0].payload

    return (
      <div
        className="bg-white border rounded p-2 shadow-sm"
        style={{ maxWidth: '280px' }}
      >
        <div className="fw-semibold">{point.label}</div>
        <div className="small mb-2">User Count: {point.userCount}</div>
        <div className="small text-muted mb-1">Top Users</div>
        {point.topUsers.length > 0 ? (
          <ul className="mb-0 ps-3 small">
            {point.topUsers.map((user) => (
              <li key={user.id}>
                {user.label}
                {user.email ? ` (${user.email})` : ''}
              </li>
            ))}
          </ul>
        ) : (
          <div className="small text-muted">No users</div>
        )}
      </div>
    )
  }

  const renderFlowContent = (data, emptyMessage) => {
    if (flowLoading) {
      return (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">
              Loading user journey data...
            </span>
          </div>
          <p className="mt-3 text-muted">Loading flow visualization...</p>
        </div>
      )
    }

    if (flowError) {
      return (
        <div className="alert alert-warning" role="alert">
          <div className="d-flex align-items-start">
            <div className="flex-grow-1">
              <p className="mb-2">
                <strong>Unable to load user journey data</strong>
              </p>
              <p className="small mb-0 text-muted">
                There was a problem loading the flow visualization. Please try
                again.
              </p>
            </div>
            <button
              className="btn btn-sm btn-outline-warning ms-3"
              onClick={onRefreshFlow}
              type="button"
            >
              Retry
            </button>
          </div>
        </div>
      )
    }

    if (data && data.nodes && data.nodes.length > 0) {
      return (
        <>
          <div className="d-none d-lg-block">
            <UserJourneySankey data={data} />
          </div>
          <div className="d-lg-none text-center text-muted py-4">
            <i className="bi bi-laptop" style={{ fontSize: '3rem' }}></i>
            <p className="mt-3">
              <strong>Best viewed on desktop</strong>
            </p>
            <p className="small mb-0">
              The user journey flow diagram is optimized for larger screens.
              Please view this page on a desktop or tablet for the best
              experience.
            </p>
          </div>
        </>
      )
    }

    return (
      <div className="text-center text-muted py-4">
        <i
          className="bi bi-diagram-3"
          style={{ fontSize: '3rem', opacity: 0.3 }}
        ></i>
        <p className="mt-3">{emptyMessage}</p>
        <p className="small mb-0">
          Flow data will appear here once users navigate through the
          application.
        </p>
      </div>
    )
  }

  return (
    <>
      {/* Date Filter */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card analytics-overview-card">
            <div className="card-body p-4">
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
                <div>
                  <h4 className="card-title mb-1">System Analytics</h4>
                  <small className="text-muted">
                    User engagement, behavior, and health metrics in one view
                  </small>
                </div>
                <div className="analytics-date-filter">
                  <label htmlFor="dateFilter" className="form-label small mb-1">
                    Time Range
                  </label>
                  <select
                    id="dateFilter"
                    className="form-select form-select-sm"
                    value={dateFilter}
                    onChange={onDateFilterChange}
                  >
                    <option value="7">Last 7 days</option>
                    <option value="30">Last 30 days</option>
                    <option value="90">Last 90 days</option>
                  </select>
                </div>
              </div>

              <div className="analytics-summary-grid">
                <dl className="analytics-summary-item mb-0">
                  <dt className="analytics-summary-label">Total Events</dt>
                  <dd className="analytics-summary-value mb-0">
                    {(metrics?.total_events || 0).toLocaleString()}
                  </dd>
                  <dd className="analytics-summary-footnote mb-0">
                    Captured in last {dateFilter} days
                  </dd>
                </dl>
                <dl className="analytics-summary-item mb-0">
                  <dt className="analytics-summary-label">Active User-Days</dt>
                  <dd className="analytics-summary-value mb-0">
                    {activeUserDays.toLocaleString()}
                  </dd>
                  <dd className="analytics-summary-footnote mb-0">
                    Across {metrics?.total_days || 0} tracked days
                  </dd>
                </dl>
                <dl className="analytics-summary-item mb-0">
                  <dt className="analytics-summary-label">Top Feature</dt>
                  <dd className="analytics-summary-value analytics-summary-feature mb-0">
                    {topFeature?.tag || 'No activity yet'}
                  </dd>
                  <dd className="analytics-summary-footnote mb-0">
                    {topFeature
                      ? `${parseInt(topFeature.usage_count).toLocaleString()} events · ${parseInt(topFeature.unique_users).toLocaleString()} users`
                      : 'Waiting for usage data'}
                  </dd>
                </dl>
                <dl className="analytics-summary-item mb-0">
                  <dt className="analytics-summary-label">Active Features</dt>
                  <dd className="analytics-summary-value mb-0">
                    {metrics?.top_features?.length || 0}
                  </dd>
                  <dd className="analytics-summary-footnote mb-0">
                    Features generating usage
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        {/* Daily Active Users */}
        <div className="col-lg-7 mb-4">
          <div className="card h-100">
            <div className="card-header">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h5 className="card-title mb-0">📈 Daily Active Users</h5>
                <div className="form-check form-switch m-0">
                  <input
                    id="excludeAdminTraffic"
                    className="form-check-input"
                    type="checkbox"
                    checked={excludeAdminTraffic}
                    onChange={(e) => setExcludeAdminTraffic(e.target.checked)}
                  />
                  <label
                    className="form-check-label small"
                    htmlFor="excludeAdminTraffic"
                  >
                    Exclude Admin Traffic (User ID: {ADMIN_USER_ID})
                  </label>
                </div>
              </div>
            </div>
            <div className="card-body">
              {dailyActiveUsersLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">
                      Loading daily active users...
                    </span>
                  </div>
                </div>
              ) : dauSeries.length > 0 ? (
                <>
                  <div className="row g-2 mb-3">
                    <div className="col-12 col-md-4">
                      <div className="border rounded p-2 h-100">
                        <div className="small text-muted">Current Day DAU</div>
                        <div className="h5 mb-0">{dauSummary.currentDay}</div>
                      </div>
                    </div>
                    <div className="col-12 col-md-4">
                      <div className="border rounded p-2 h-100">
                        <div className="small text-muted">Average DAU</div>
                        <div className="h5 mb-0">
                          {dauSummary.average.toFixed(1)}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-4">
                      <div className="border rounded p-2 h-100">
                        <div className="small text-muted">Period Change</div>
                        <div
                          className={`h5 mb-0 ${dauSummary.changePct >= 0 ? 'text-success' : 'text-danger'}`}
                        >
                          {dauSummary.changePct >= 0 ? '+' : ''}
                          {dauSummary.changePct.toFixed(1)}%
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={dauSeries}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" />
                        <YAxis allowDecimals={false} />
                        <Tooltip content={<DAUTooltip />} />
                        <Area
                          type="monotone"
                          dataKey="userCount"
                          stroke="#0d6efd"
                          fill="#0d6efd"
                          fillOpacity={0.2}
                          name="Daily Active Users"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </>
              ) : (
                <div className="text-center text-muted py-4">
                  <p>No user activity data available</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Top Features */}
        <div className="col-lg-5 mb-4">
          <div className="card h-100">
            <div className="card-header">
              <h5 className="card-title mb-0">🏆 Most Used Features</h5>
            </div>
            <div className="card-body">
              {metrics?.top_features?.length > 0 ? (
                <div className="list-group list-group-flush analytics-feature-list">
                  {metrics.top_features.slice(0, 10).map((feature, index) => {
                    const category = getTagCategory(feature.tag)
                    return (
                      <div
                        key={index}
                        className="list-group-item d-flex justify-content-between align-items-center px-0"
                      >
                        <div>
                          <div className="d-flex align-items-center">
                            <span
                              className={`badge bg-${getTagCategoryColor(
                                category,
                              )} me-2`}
                            >
                              {category}
                            </span>
                            <code className="feature-tag">{feature.tag}</code>
                          </div>
                          <small className="text-muted">
                            {feature.unique_users} unique users
                          </small>
                        </div>
                        <span className="badge bg-primary rounded-pill">
                          {feature.usage_count}
                        </span>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="text-center text-muted py-4">
                  <p>No feature usage data available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Daily Active Users Table */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <h5 className="card-title mb-0">
                    👥 Daily Active Users - Detailed View
                  </h5>
                  <small className="text-muted">
                    Most recent date auto-expands when opened
                  </small>
                </div>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  type="button"
                  onClick={handleShowDetailedDau}
                  aria-expanded={showDetailedDau}
                  aria-controls="dau-detailed-table"
                >
                  {showDetailedDau ? 'Hide Details' : 'Show Details'}
                </button>
              </div>
            </div>
            <div className="card-body">
              {!showDetailedDau ? (
                <p className="text-muted mb-0">
                  Expand to inspect users for specific days.
                </p>
              ) : dailyActiveUsersLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">
                      Loading detailed user data...
                    </span>
                  </div>
                </div>
              ) : dailyActiveUsersDetailed.length > 0 ? (
                <div className="table-responsive" id="dau-detailed-table">
                  <table className="table table-sm table-hover">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '150px' }}>Date</th>
                        <th style={{ width: '100px' }}>User Count</th>
                        <th>User Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dailyActiveUsersDetailed.map((day, index) => (
                        <React.Fragment key={index}>
                          <tr>
                            <td>
                              <strong>{formatDate(day.date)}</strong>
                            </td>
                            <td>
                              <span className="badge bg-primary rounded-pill">
                                {excludeAdminTraffic
                                  ? (day.user_ids || []).filter(
                                      (id) => String(id) !== ADMIN_USER_ID,
                                    ).length
                                  : day.user_count}
                              </span>
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                type="button"
                                onClick={() => toggleExpandedDate(day.date)}
                                aria-expanded={Boolean(expandedDates[day.date])}
                                aria-controls={`dau-day-${day.date}`}
                              >
                                {expandedDates[day.date]
                                  ? 'Hide Users'
                                  : 'View Users'}
                              </button>
                            </td>
                          </tr>
                          {expandedDates[day.date] && (
                            <tr id={`dau-day-${day.date}`}>
                              <td colSpan={3}>
                                <div className="d-flex flex-column gap-1">
                                  {(excludeAdminTraffic
                                    ? (day.user_ids || []).filter(
                                        (id) => String(id) !== ADMIN_USER_ID,
                                      )
                                    : day.user_ids || []
                                  ).map((userId) => {
                                    const player = playerMap.get(String(userId))
                                    const name =
                                      `${player?.first_name || ''} ${player?.last_name || ''}`.trim()
                                    return (
                                      <div key={userId} className="small">
                                        <strong>
                                          {name || `User ${userId}`}
                                        </strong>
                                        {player?.email
                                          ? ` • ${player.email}`
                                          : ''}
                                      </div>
                                    )
                                  })}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center text-muted py-4">
                  <p>No daily active user data available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Events by User */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">📊 Events by User</h5>
              <small className="text-muted">
                Top users by event count in the selected time range (excludes
                analytics_events)
              </small>
            </div>
            <div className="card-body">
              {eventsByUserLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">
                      Loading events by user...
                    </span>
                  </div>
                </div>
              ) : eventsByUser.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-sm table-hover">
                    <caption className="visually-hidden">
                      Top users by event count
                    </caption>
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '60px' }}>Rank</th>
                        <th style={{ width: '270px' }}>User</th>
                        <th>Event Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eventsByUser.map((userEvent, index) => {
                        // Determine display name: firstname + lastname → email → user_id
                        const fullName = [
                          userEvent.firstname,
                          userEvent.lastname,
                        ]
                          .filter(Boolean)
                          .join(' ')
                        const displayName =
                          fullName ||
                          userEvent.email ||
                          `User ${userEvent.user_id}`

                        // Build informative tooltip
                        const tooltipParts = [`User ID: ${userEvent.user_id}`]
                        if (!fullName) {
                          tooltipParts.push('No name on record')
                        }
                        if (fullName && !userEvent.email) {
                          tooltipParts.push('No email on record')
                        }
                        const tooltip = tooltipParts.join(' • ')

                        return (
                          <tr key={userEvent.user_id || index}>
                            <td>
                              <span className="badge bg-secondary">
                                #{index + 1}
                              </span>
                            </td>
                            <td>
                              <button
                                className="btn btn-link btn-sm text-start p-0"
                                onClick={() =>
                                  onJumpToUser?.(userEvent.user_id)
                                }
                                aria-label={`View details for ${displayName}`}
                                style={{
                                  border: 'none',
                                  cursor: 'pointer',
                                  textDecoration: 'none',
                                }}
                                title={tooltip}
                              >
                                <strong>{displayName}</strong>
                                {fullName && userEvent.email && (
                                  <div>
                                    <small className="text-muted">
                                      {userEvent.email}
                                    </small>
                                  </div>
                                )}
                              </button>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <span className="badge bg-primary rounded-pill me-2">
                                  {parseInt(
                                    userEvent.event_count,
                                  ).toLocaleString()}
                                </span>
                                <div
                                  className="progress flex-grow-1"
                                  style={{ height: '8px', maxWidth: '300px' }}
                                  aria-label={`Event count progress for ${displayName}`}
                                >
                                  <div
                                    className="progress-bar"
                                    role="progressbar"
                                    style={{
                                      width: `${
                                        eventsByUser[0] &&
                                        !isNaN(
                                          parseInt(eventsByUser[0].event_count),
                                        ) &&
                                        parseInt(eventsByUser[0].event_count) >
                                          0
                                          ? Math.min(
                                              100,
                                              (parseInt(userEvent.event_count) /
                                                parseInt(
                                                  eventsByUser[0].event_count,
                                                )) *
                                                100,
                                            )
                                          : 0
                                      }%`,
                                    }}
                                    aria-valuenow={parseInt(
                                      userEvent.event_count,
                                    )}
                                    aria-valuemin="0"
                                    aria-valuemax={
                                      eventsByUser[0]
                                        ? parseInt(eventsByUser[0].event_count)
                                        : 0
                                    }
                                  ></div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center text-muted py-4">
                  <p>No events by user data available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sankey Filters */}
      <div className="row mb-3">
        <div className="col-12">
          <div className="analytics-flow-filter-wrap">
            <div className="analytics-flow-filter-label-wrap">
              <label
                htmlFor="sankeySearchFilter"
                className="form-label mb-1 fw-semibold text-nowrap"
              >
                Filter Flow Data
              </label>
              <small className="text-muted d-block">
                Narrow the journey diagrams to a specific user or context
              </small>
            </div>
            <div className="flex-grow-1 analytics-flow-filter-input-wrap">
              <div className="input-group sankey-input-group">
                <input
                  id="sankeySearchFilter"
                  type="text"
                  className="form-control"
                  placeholder="Type league, division, player name, email, or user ID"
                  value={flowSearchTerm}
                  onChange={(e) => onFlowSearchChange?.(e.target.value)}
                  data-testid="sankey-search-filter"
                />
                {flowSearchTerm && (
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={() => onFlowSearchChange?.('')}
                    data-testid="clear-sankey-search"
                    aria-label="Clear search filter"
                  >
                    ×
                  </button>
                )}
              </div>
              <small className="text-muted d-block mt-2">
                {flowSearchHint ||
                  'Filter User Journey Flow by league, division, player, email, or user ID'}
              </small>
            </div>
          </div>
        </div>
      </div>

      {/* Landing & Auth Flow - Only show when no filter active */}
      {!flowSearchTerm && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0">
                  🔐 Landing, Sign In, & Sign Up Flow
                </h5>
                <p className="text-muted small mb-0">
                  Entry-point navigation between marketing and authentication
                  pages
                </p>
              </div>
              <div className="card-body">
                {renderFlowContent(
                  landingAuthFlow,
                  'No landing or authentication flow data available',
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Journey Flow */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">
                🗺️ User Journey Flow
                {flowSearchTerm && (
                  <span className="badge bg-primary ms-2">Filtered</span>
                )}
              </h5>
              <p className="text-muted small mb-0">
                Common navigation paths through the application
                {flowSearchTerm && (
                  <span className="text-primary">
                    {' '}
                    · Showing flows for: &quot;{flowSearchTerm}&quot;
                  </span>
                )}
              </p>
            </div>
            <div className="card-body">
              {renderFlowContent(journeyFlow, 'No user journey data available')}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

MetricsDashboardSection.propTypes = {
  metrics: PropTypes.shape({
    total_events: PropTypes.number,
    total_days: PropTypes.number,
    daily_active_users: PropTypes.arrayOf(
      PropTypes.shape({
        date: PropTypes.string,
        daily_active_users: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]),
      }),
    ),
    top_features: PropTypes.arrayOf(
      PropTypes.shape({
        tag: PropTypes.string,
        usage_count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        unique_users: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    ),
  }),
  dailyActiveUsersDetailed: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string,
      user_count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      user_ids: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      ),
    }),
  ),
  dailyActiveUsersLoading: PropTypes.bool,
  players: PropTypes.arrayOf(PropTypes.object),
  eventsByUser: PropTypes.arrayOf(
    PropTypes.shape({
      user_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      firstname: PropTypes.string,
      lastname: PropTypes.string,
      email: PropTypes.string,
      event_count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  eventsByUserLoading: PropTypes.bool,
  flowData: PropTypes.shape({
    nodes: PropTypes.array,
    links: PropTypes.array,
  }),
  flowLoading: PropTypes.bool,
  flowError: PropTypes.string,
  dateFilter: PropTypes.string,
  onDateFilterChange: PropTypes.func,
  onJumpToUser: PropTypes.func,
  flowSearchTerm: PropTypes.string,
  onFlowSearchChange: PropTypes.func,
  flowSearchHint: PropTypes.string,
  onRefreshFlow: PropTypes.func,
}

export default MetricsDashboardSection
